/**
 * Instances page
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';

import ORTypography from 'components/ORTypography/ORTypography';
import LoadingPlaceHolder from 'components/LoadingPlaceholder/LoadingPlaceholder';
import Poll from 'components/Poll/Poll';
import ORv4Link from 'components/ORv4Link/ORv4LinkContainer';
import FlagEnabledContainer from 'components/FlagEnabled/FlagEnabledContainer';
import ORContentHeader from 'components/ORContentHeader/ORContentHeader';
import ORContentArea from 'components/ORContentArea/ORContentArea';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import NoInstancesPageContainer from './NoInstancesPage/NoInstancesPageContainer';
import InstanceItemContainer from './InstanceItem/InstanceItemContainer';
import InstanceItemORv4Container from './InstanceItem/InstanceItemORv4Container';

const propTypes = {
  catalog: PropTypes.shape({}).isRequired,
  getInstancesPoll: PropTypes.func.isRequired,
  instancesORv4: PropTypes.shape({
    instancesORv4: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  instancesState: PropTypes.shape({
    instances: PropTypes.shape({}).isRequired,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  cockroachdbMultiInstanceIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const maturity = (catalog, instance) => {
  const service = catalog.servicesCatalog.services.find(
    (catalogService) => catalogService.name === instance.service,
  );
  // If a service is set to display none, then it won't be available in the catalog. However,
  // instances of that service can still be created by developers. This lets those instances show up
  // in the UI without an error.
  // 1 === MATURITY_ALPHA
  if (typeof service === 'undefined') {
    return 1;
  }
  const { maturityLevel } = service;
  return maturityLevel;
};

const instanceORv5ListBuilder = (list, catalog, serviceMap, instancesByService) => {
  if (list.length) {
    list
      .sort((a, b) => (a.name > b.name ? 1 : -1))
      .forEach((instance) => {
        Object.keys(serviceMap).forEach((service) => {
          if (serviceMap[service].includes(instance.service)) {
            instancesByService[service].push(
              <InstanceItemContainer
                serviceMaturity={maturity(catalog, instance)}
                {...instance}
                key={instance.id}
              />,
            );
          }
        });
      });
  }
  return instancesByService;
};

const instanceORv4ListBuilder = (data, serviceMap, instancesByService) => {
  if (data.instancesORv4.length) {
    data.instancesORv4
      .sort((a, b) => (a.name > b.name ? 1 : -1))
      .forEach((item) => {
        Object.keys(serviceMap).forEach((service) => {
          if (serviceMap[service].includes(item.service)) {
            instancesByService[service].push(
              <FlagEnabledContainer key={item.id} flagKey="showORv4">
                <InstanceItemORv4Container {...item} subtype={item.type} key={item.id} />
              </FlagEnabledContainer>,
            );
          }
        });
      });
  }
  return instancesByService;
};

/* Hard coding normalizing, sorting and filtering into the UI for each service.
   TODO: Add some sort of sane default to catch new services as they're added.
*/
const instancesBuilder = (instancesORv4, instancesORv5, catalog, classes, crdbIds) => {
  const serviceMap = {
    MongoDB: ['mongodb', 'MongoDB'],
    Redis: ['redis', 'Redis'],
    Elasticsearch: ['elasticsearch', 'Elasticsearch'],
    CockroachDB: ['CockroachDB'],
    PostgreSQL: ['PostgreSQL'],
    TimescaleDB: ['TimescaleDB'],
  };

  /* If the service matches one of the service types above it will get added to the list of
  instances for that service */
  let instancesByService = {
    MongoDB: [],
    CockroachDB: [],
    Redis: [],
    Elasticsearch: [],
    PostgreSQL: [],
    TimescaleDB: [],
  };

  instancesByService = instanceORv4ListBuilder(instancesORv4, serviceMap, instancesByService);
  instancesByService = instanceORv5ListBuilder(
    instancesORv5,
    catalog,
    serviceMap,
    instancesByService,
  );

  const crdbs = instancesByService.CockroachDB.slice();

  instancesByService.CockroachDB = crdbIds.reduce((acc, crdbId) => {
    const headerComponent = crdbId ? (
      <ORTypography key={crdbId} variant="h6" component="h2" className={classes.secondaryHeader}>
        Group: {crdbId}
      </ORTypography>
    ) : null;
    const crdbInstances = crdbs.filter((crdb) => crdb.props.metadata.multiInstanceId === crdbId);
    const newcrdbServices = [...acc, headerComponent, ...crdbInstances];

    return newcrdbServices;
  }, []);

  const instancesListWithHeadings = [];

  Object.keys(instancesByService).forEach((service) => {
    if (instancesByService[service].length) {
      instancesListWithHeadings.push(
        <ORTypography key={service} variant="h5" component="h1" className={classes.secondaryHeader}>
          {service}
        </ORTypography>,
      );
      instancesByService[service].forEach((instanceComponent) =>
        instancesListWithHeadings.push(instanceComponent),
      );
    }
  });

  return instancesListWithHeadings;
};

export function UnstyledInstancesPage({
  getInstancesPoll,
  instancesState,
  cockroachdbMultiInstanceIDs,
  instancesORv4,
  catalog,
  classes,
}) {
  const orv5instances = instancesState.instances || {};
  return (
    <React.Fragment>
      <ORContentHeader hasDivider>Instances</ORContentHeader>
      <Poll intervalInMS={15000} actionCreator={getInstancesPoll} />
      {!instancesState.isLoading && !instancesORv4.isLoading ? (
        <React.Fragment>
          <ORContentArea>
            <FlagEnabledContainer flagKey="showORv4" negate>
              {instancesORv4.instancesORv4.length === 0 && (
                <div style={{ margin: '16px 0' }}>
                  <SegmentClickTracker buttonName="ORv4 SSO (Instance List)">
                    <ORv4Link />
                  </SegmentClickTracker>
                </div>
              )}
            </FlagEnabledContainer>
            {Object.values(orv5instances).length + instancesORv4.instancesORv4.length ? (
              instancesBuilder(
                instancesORv4,
                Object.values(orv5instances),
                catalog,
                classes,
                cockroachdbMultiInstanceIDs,
              )
            ) : (
              <NoInstancesPageContainer />
            )}
          </ORContentArea>
        </React.Fragment>
      ) : (
        <LoadingPlaceHolder />
      )}
    </React.Fragment>
  );
}

UnstyledInstancesPage.propTypes = propTypes;

const styles = (theme) => ({
  secondaryHeader: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
});
export default withStyles(styles)(UnstyledInstancesPage);
